import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDirectoryUsers } from '@/features/access/api'
import { useAuth } from '@/lib/auth/AuthProvider'
import { api } from '@/lib/api/client'
import { loadProject, mapSummary, rowsOf } from './map'
import type { ProjectInput, ReleaseInput, StaffUser, TeamMemberInput } from './types'

export const projectKeys = {
  all: ['projects'] as const,
  detail: (id: string) => ['projects', id] as const,
  staff: ['projects', 'staff'] as const,
  catalog: ['projects', 'catalog'] as const,
}

export function useActor() {
  const { user } = useAuth()
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—'
}

/** projectId verilirse yalnız o projenin detayı ve liste (kart özetleri) yenilenir. */
export function useInvalidateProjects(projectId?: string) {
  const qc = useQueryClient()
  return () =>
    projectId
      ? Promise.all([qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }), qc.invalidateQueries({ queryKey: projectKeys.all, exact: true })])
      : qc.invalidateQueries({ queryKey: projectKeys.all })
}

/** Liste kart özetleri (görev/kurulum/üye) sunucuda hesaplanır; görevler ayrıca çekilmez. */
export const useProjects = () =>
  useQuery({
    queryKey: projectKeys.all,
    queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/projects')).map((row) => mapSummary(row)),
  })

export const useProject = (id: string | null | undefined) => useQuery({ queryKey: projectKeys.detail(id ?? ''), queryFn: () => loadProject(id as string), enabled: !!id })

/** Personel listesi Erişim modülüyle aynı önbellekten (kullanıcı dizini). */
export function useStaff() {
  const users = useDirectoryUsers()
  const data = useMemo<StaffUser[] | undefined>(
    () => users.data?.filter((u) => u.isActive).map((u) => ({ id: u.id, name: u.name, position: u.position || u.roleNames[0] || '' })),
    [users.data],
  )
  return { ...users, data }
}

export const useMyTaskSummary = () => useQuery({ queryKey: [...projectKeys.all, 'mine'], queryFn: () => api.get<unknown>('/api/v1/tasks/mine/summary') })

export function useSaveProject() {
  const invalidate = useInvalidateProjects()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: ProjectInput }) => {
      if (!id) {
        const saved = await api.post<Record<string, unknown>>('/api/v1/projects', {
          name: input.name,
          code: input.key,
          description: input.description,
          status: input.status,
          deployment_type: 'customer_server',
          list_price: 0,
          icon: input.icon,
          color_code: input.color,
          start_date: input.startDate || undefined,
          end_date: input.endDate || undefined,
          kind: input.kind,
          sector_id: input.sectorId,
        })
        return { id: String(saved.id ?? '') }
      }
      await api.put(`/api/v1/projects/${id}`, {
        name: input.name,
        description: input.description,
        icon: input.icon,
        color_code: input.color,
        status: input.status,
        code: input.key,
        start_date: input.startDate,
        // Boş bitiş "süresiz" demektir (iç proje); özel projede sunucu bitişi zorunlu tutar.
        end_date: input.endDate,
        kind: input.kind,
        sector_id: input.sectorId || undefined,
      })
      return { id }
    },
    onSuccess: invalidate,
  })
}

export function useDeleteProject() {
  const invalidate = useInvalidateProjects()
  return useMutation({ mutationFn: (id: string) => api.post(`/api/v1/projects/${id}/archive`, {}), onSuccess: invalidate })
}

export function useSaveMember(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: TeamMemberInput }) => {
      // Üye kaydı kullanıcı başına tektir; düzenleme eski kaydı (id = önceki kullanıcı) kaldırıp yeniden ekler.
      if (id) await api.del(`/api/v1/projects/${projectId}/members/${id}`)
      return api.post(`/api/v1/projects/${projectId}/members`, { user_id: input.userId, area: input.discipline, title: input.title, is_lead: input.discipline === 'management' })
    },
    onSuccess: invalidate,
  })
}

export function useRemoveMember(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({ mutationFn: (userId: string) => api.del(`/api/v1/projects/${projectId}/members/${userId}`), onSuccess: invalidate })
}

export function useSaveRelease(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: ReleaseInput }) => {
      const body = { version: input.version, channel: input.channel, released_on: input.releasedAt, notes: input.notes }
      return id ? api.put(`/api/v1/projects/${projectId}/releases/${id}`, body) : api.post(`/api/v1/projects/${projectId}/releases`, body)
    },
    onSuccess: invalidate,
  })
}

export function useDeleteRelease(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({ mutationFn: (releaseId: string) => api.del(`/api/v1/projects/${projectId}/releases/${releaseId}`), onSuccess: invalidate })
}
